/**
 * Lo que cuesta cada plan y la oferta de beta, escritos una sola vez.
 *
 * Vivían dentro de la pantalla de planes, y el aviso de tope tenía los suyos a
 * mano en el catálogo de textos: decía «desde $9.50/mes» para Pro y «desde
 * $19.50» para Business cuando la pantalla ya cobraba otra cosa. Un precio que
 * se promete en dos sitios acaba siendo dos precios.
 *
 * Son el precio de LISTA en dólares por mes. Lo que se cobra de verdad sale de
 * los `price_...` de Stripe (ver `STRIPE_PRICE_*` en el worker); esto tiene que
 * coincidir con ellos, y al cambiar uno hay que cambiar el otro.
 */
export const PLAN_PRICES = {
  pro: { annual: 17, monthly: 21 },
  business: { annual: 37, monthly: 46 },
  /* Elite todavía no se vende: su precio es de la tarjeta de «Próximamente». */
  elite: { annual: 99, monthly: 125 },
} as const

/**
 * Descuento de lanzamiento vigente durante la Beta. Aplica a todos los planes
 * de pago, pero SÓLO en el ciclo anual: el worker no adjunta el cupón a un pago
 * mensual (ver `routes/billing.ts`), así que aquí tampoco se promete.
 *
 * Es el MULTIPLICADOR, no el descuento: 0.6 quiere decir que se paga el 60%, o
 * sea un 40% menos. Se guarda así porque es como se usa —`precio × esto`— y
 * porque invertirlo cada vez es la clase de cuenta que un día sale al revés.
 */
export const BETA_DISCOUNT = 0.6

/**
 * Un importe en dólares para pantalla: «21», «10.20», «1,188».
 *
 * Sin decimales cuando es entero y con dos cuando no. Existe porque 17 × 0.6 × 12
 * en coma flotante da 122.39999999999999, y eso es lo que se llegó a enseñar en
 * la pantalla donde se autoriza el cobro. Redondea a centavos antes de pintar.
 */
export function formatUsd(amount: number): string {
  const cents = Math.round(amount * 100) / 100
  return cents.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(cents) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

/**
 * El descuento como porcentaje, para los textos. Derivado y no escrito a mano:
 * el día que se falle uno, la página promete un descuento y cobra otro.
 */
export const BETA_OFF_PERCENT = Math.round((1 - BETA_DISCOUNT) * 100)

/**
 * Cuántos meses dura el precio de beta. El valor de verdad está en el cupón de
 * Stripe (`STRIPE_BETA_PROMOTION_CODE`); esto sólo lo cuenta, así que al
 * cambiar la duración allí hay que cambiarla aquí.
 */
export const BETA_MONTHS = 12

/**
 * El precio más bajo al que se puede tener un plan: el anual con beta, por mes.
 * Es el «desde» de los avisos que venden el plan.
 */
export function lowestMonthlyPrice(plan: keyof typeof PLAN_PRICES): number {
  return Math.round(PLAN_PRICES[plan].annual * BETA_DISCOUNT * 100) / 100
}
